import classNames from 'classnames';
import { Spinner } from 'reactstrap';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { type EmissionCalculation } from '../../types/emissions';
import {
  formatCO2e,
  formatDate,
  formatDateShort,
  formatFactorValue,
  formatProvider,
  formatSource,
  STATUS_CONFIG,
} from './formatters';
import { useMemo } from 'react';
import { useGetInitiativeUsersQuery } from '../../../../api/manage-users';

interface ActivityHistoryViewProps {
  calculations: EmissionCalculation[];
  isLoading: boolean;
  showInTonnes: boolean;
  decimalPlaces: number;
  onRevert: (targetVersion: number) => void;
  isReverting: boolean;
}

export const ActivityHistoryView = ({
  calculations,
  isLoading,
  showInTonnes,
  decimalPlaces,
  onRevert,
  isReverting,
}: ActivityHistoryViewProps) => {
  const head = calculations[0];
  const { data = { users: [] } } = useGetInitiativeUsersQuery(
    { initiativeId: head?.initiativeId ?? '', includeSubsidiaries: false },
    { skip: !head?.isFactorOverride },
  );
  const user = useMemo(
    () => data.users.find((u) => u._id === head?.factorOverriddenBy),
    [data.users, head?.factorOverriddenBy],
  );

  if (isLoading) {
    return (
      <div className='py-5 text-center'>
        <Spinner color='success' size='sm' />
        <p className='text-ThemeTextLight mt-2 mb-0'>Loading calculation history...</p>
      </div>
    );
  }

  if (calculations.length === 0) {
    return (
      <div className='py-5 text-center text-ThemeTextLight'>
        <i className='fa fa-history fa-2x mb-2 opacity-50' />
        <p className='mb-0'>No calculation history available</p>
      </div>
    );
  }

  return (
    <div>
      <div className='calculation-history'>
        {calculations.map((calc, index) => {
          const statusConfig = STATUS_CONFIG[calc.status];
          const isCurrent = index === 0;

          return (
            <div key={calc._id} className={classNames('border rounded p-3 mb-2', { 'border-success': isCurrent })}>
              <div className='d-flex justify-content-between align-items-start mb-2'>
                <div>
                  <Badge className={cn(statusConfig.className, 'tw:me-2')}>{statusConfig.label}</Badge>
                  {isCurrent && (
                    <Badge className='tw:bg-emerald-500/10 tw:text-emerald-700 tw:border-emerald-200'>Current</Badge>
                  )}
                </div>
                <span className='text-ThemeTextLight'>v{calc.version}</span>
              </div>
              <div className='d-flex justify-content-between align-items-center'>
                <span className='fw-bold'>{formatCO2e(calc.co2e, showInTonnes, decimalPlaces)}</span>
                <span className='text-ThemeTextLight'>{formatDate(calc.calculatedAt)}</span>
              </div>
              <div className='mt-2 text-ThemeTextLight d-flex flex-wrap gap-2' style={{ fontSize: '0.75rem' }}>
                <span>
                  <i className='fa fa-database me-1' />
                  {formatSource(calc.emissionFactorSource)}
                  {calc.emissionFactorYear && <span className='ms-1'>({calc.emissionFactorYear})</span>}
                </span>
                <span>
                  <i className='fa fa-flask me-1' />
                  {calc.gwpMethodology}
                </span>
                {calc.emissionFactorProvider && (
                  <span>
                    <i className='fa fa-plug me-1' />
                    {formatProvider(calc.emissionFactorProvider)}
                  </span>
                )}
                {calc.emissionFactorValue > 0 && (
                  <span className='font-monospace'>
                    {formatFactorValue(calc.emissionFactorValue)} {calc.emissionFactorUnit}
                  </span>
                )}
              </div>
              {calc.isFactorOverride && (
                <div className='tw:mt-2 tw:text-xs tw:text-blue-700 tw:flex tw:items-center tw:gap-1'>
                  <i className='fa fa-user-edit' />
                  <span>
                    Manually overridden
                    {calc.factorOverriddenBy && user && <> by {user?.firstName} {user?.surname}</>}
                    {calc.factorOverriddenAt && <> on {formatDateShort(calc.factorOverriddenAt)}</>}
                  </span>
                </div>
              )}
              {calc.isRevert && (
                <div className='tw:mt-2 tw:text-xs tw:text-amber-700 tw:flex tw:items-center tw:gap-1'>
                  <i className='fa fa-undo' />
                  <span>
                    Reverted from v{calc.revertedFromVersion}
                    {calc.revertedAt && <> on {formatDateShort(calc.revertedAt)}</>}
                  </span>
                </div>
              )}
              {!isCurrent && (
                <div className='tw:mt-2 tw:flex tw:justify-end'>
                  <Button
                    variant='outline'
                    size='xs'
                    onClick={() => onRevert(calc.version)}
                    disabled={isReverting}
                  >
                    <i className='fa fa-undo tw:mr-1' />
                    Revert to this version
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
